import posthog, { type CaptureResult } from 'posthog-js'
import type { Me } from '@region13/shared-types'
import { CLIENT_ERROR_KINDS, CLIENT_ERROR_MESSAGE_CAP, type AnalyticsEvent, type EventProps } from './events'
import {
  BLOCKED_EVENTS, POSTHOG_API_HOST, POSTHOG_PRIVACY_CONFIG, POSTHOG_UI_HOST, SURFACE_CONFIG,
  URL_QUERY_PATTERN, URL_SCRUB_MAX_DEPTH, type Surface,
} from './surfaces'

let active: Surface | null = null
let identified: string | null = null

/** ⚠️ THE NO-KEY GUARD IS THE WHOLE STAGING STORY. There is no ENVIRONMENT
 *  check anywhere in this package: staging and local development are silent
 *  because their builds carry no key, so this returns false and every
 *  `capture`/`identifyUser` below is inert. An empty string counts as no key —
 *  Vite substitutes '' for an unset var on some paths.
 *
 *  Returns whether analytics is now live, so a caller can log it in dev. */
export function initAnalytics(opts: { surface: Surface; key: string | undefined }): boolean {
  if (!opts.key) return false
  if (active) return true
  posthog.init(opts.key, {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    // ⚠️ NEVER TURN THESE TWO ON. Autocapture being off is what structurally
    // guarantees no child's name, no jersey number and no typed free text
    // reaches PostHog: nothing is ever scraped, so no component can leak by
    // forgetting to mask. Session recording is off for the same reason. Three
    // of these surfaces render children's names.
    autocapture: false,
    disable_session_recording: true,
    ...POSTHOG_PRIVACY_CONFIG,
    before_send: beforeSend,
    // Heatmaps need no autocapture and record positions rather than text.
    capture_heatmaps: SURFACE_CONFIG[opts.surface].heatmaps,
    // Anonymous readers create no person profile until they sign in, at which
    // point their prior anonymous history merges into the identified person.
    person_profiles: 'identified_only',
    defaults: '2026-05-30',
  })
  // ⚠️ THE SURFACE IS A SUPER PROPERTY, not only a per-event one. Without this
  // register, every AUTOMATIC event — `$pageview`, `$pageleave`, `$identify`,
  // `$web_vitals` — arrives with no surface at all, and only the ten named
  // events are labeled. `$pageview` is the first thing an analyst reaches for.
  //
  // It also overwrites whatever another surface left behind in this origin's
  // persistence. admin-spa and the SSR public site are both served from
  // schedule.ayso13.org on the same project token, so they share one
  // `ph_<token>_posthog` localStorage key, and the public site's snippet
  // registers `surface: 'public'` into it. An operator who read a public
  // schedule page and then opened /admin would otherwise have every admin
  // `$pageview` labeled `public` — exactly the merge the spec forbids.
  posthog.register({ surface: opts.surface })
  active = opts.surface
  installErrorHandler()
  return true
}

/** `before_send`: returning null is how posthog-js drops an event. See
 *  `BLOCKED_EVENTS` for why this exists alongside the config.
 *
 *  ⚠️ NO QUERY STRING LEAVES IN AN EVENT. A `/submit?t=` token decodes to a
 *  referee's address (youth referees included) and files a report until 14 days
 *  past the game, and `?h=` is a proof of human. posthog-js copies the URL into
 *  `$current_url`, `$referrer`, `$session_entry_*`, `$initial_*` (in `$set_once`),
 *  the `$web_vitals_*_event` objects and the keys of `$heatmap_data`, on every
 *  event, `$pageleave` and `client_error` included. This hook sees events only: `/flags`
 *  is off in `POSTHOG_PRIVACY_CONFIG`, and the mask there keeps `t` and `h` out of what
 *  posthog-js stores.
 *
 *  ⚠️ IT WALKS EVERY VALUE, NOT A LIST OF THOSE NAMES. The snippet loads whatever
 *  array.js the proxy serves, which is ahead of the npm version here (1.444.0
 *  against 1.438.21 on 2026-11-01), so the names are not ours to pin. UTM tags
 *  are their own properties by this point and survive.
 *
 *  `snippet.ts` holds the same function as ES5 for the server-rendered pages, and
 *  `snippet.test.ts` runs both over one fixture list. */
function beforeSend(cr: CaptureResult | null): CaptureResult | null {
  if (!cr || BLOCKED_EVENTS.includes(cr.event)) return null
  try {
    cr.properties = scrubUrls(cr.properties, 0) as CaptureResult['properties']
    if (cr.$set) cr.$set = scrubUrls(cr.$set, 0) as CaptureResult['$set']
    if (cr.$set_once) cr.$set_once = scrubUrls(cr.$set_once, 0) as CaptureResult['$set_once']
    return cr
  } catch {
    return null
  }
}

const URL_QUERY = new RegExp(URL_QUERY_PATTERN, 'gi')

/** Strings lose their query, in values and in keys; two heatmap URLs that meet
 *  after the cut keep both sets of points. Anything but a plain object or an array
 *  passes untouched, which is what keeps a Date a Date. */
function scrubUrls(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return value.replace(URL_QUERY, '$1')
  if (depth > URL_SCRUB_MAX_DEPTH && typeof value === 'object') throw new Error('too deep')
  if (Array.isArray(value)) return value.map((v) => scrubUrls(v, depth + 1))
  if (Object.prototype.toString.call(value) !== '[object Object]') return value
  const out: Record<string, unknown> = {}
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const name = key.replace(URL_QUERY, '$1')
    const next = scrubUrls(raw, depth + 1)
    const held = out[name]
    out[name] = Array.isArray(held) && Array.isArray(next) ? held.concat(next) : next
  }
  return out
}

/** ⚠️ ONLY THE MESSAGE AND THE ROUTE. Never `event.error.stack` (it can carry
 *  interpolated values), never the page content, never a request payload —
 *  three of these surfaces render children's names.
 *
 *  The 200-character cut is a VOLUME CAP, not a redaction: truncating at 200
 *  would not remove a name that appeared in the first 200 characters. What
 *  actually keeps player data out of `client_error` is that no throw in these
 *  SPAs interpolates any — the only two template-literal throws in the SPA
 *  trees embed an ISO date string and an HTTP status code. A new one that
 *  interpolated a roster value would be a leak the slice could not stop, so
 *  check the message, not the length. */
function installErrorHandler(): void {
  const send = (message: string, kind: string) => {
    capture('client_error', {
      message: message.slice(0, CLIENT_ERROR_MESSAGE_CAP),
      kind,
      route: window.location.pathname,
    })
  }
  // `e.message` is a string on a real ErrorEvent, but any code can dispatch a
  // bare Event named 'error', and `.slice` on a non-string would throw inside
  // the listener. String() costs nothing.
  window.addEventListener('error', (e) => send(String(e.message ?? ''), CLIENT_ERROR_KINDS.thrown))
  window.addEventListener('unhandledrejection', (e) => {
    const r: unknown = e.reason
    send(r instanceof Error ? r.message : String(r), CLIENT_ERROR_KINDS.rejection)
  })
}

export function analyticsEnabled(): boolean {
  return active !== null
}

/** The sign-out hook: drops the identity, ours and PostHog's, so the next
 *  person on a shared browser does not inherit the last one's profile.
 *
 *  ⚠️ `active` IS DELIBERATELY NOT CLEARED. On coach-spa, referee-spa,
 *  fields-spa and stats-spa, sign-out does not navigate — it resets the `me`
 *  query and renders `<Login>` in place — and `initAnalytics` runs once, from
 *  `main.tsx`, per page load. Clearing `active` here silenced the surface for
 *  the rest of that page session with nothing to re-initialize it: person two
 *  on the shared laptop got no `surface_opened`, no flow events and no
 *  identity. Mis-attribution has to be prevented; person two must not be
 *  erased along with it.
 *
 *  ⚠️ ORDER MATTERS. `posthog.reset()` clears super properties as well as the
 *  distinct id, so the surface registered in `initAnalytics` has to be put
 *  back AFTER it. Registering first and resetting second would leave every
 *  automatic event for the rest of the page session unlabeled — and on
 *  schedule.ayso13.org, where admin-spa and the public site share one
 *  persistence key, unlabeled is not even the worst case. */
export function resetAnalytics(): void {
  if (active) {
    posthog.reset()
    posthog.register({ surface: active })
  }
  identified = null
}

/** ⚠️ TEST SEAM ONLY — never call this from application code. It clears
 *  `active`, which the production `resetAnalytics` above must not do, and a
 *  surface that has forgotten which surface it is sends nothing for the rest
 *  of the page session. `barrelImports.test.ts` fails if any app source tree
 *  references it. */
export function __resetForTests(): void {
  active = null
  identified = null
}

/** Every event carries its surface, so one project can answer "who uses what"
 *  without a second project splitting one person into two. */
export function capture(event: AnalyticsEvent, props?: EventProps): void {
  if (!active) return
  posthog.capture(event, { ...props, surface: active })
}

/** ⚠️ THE IDENTIFIER IS THE SESSION'S OWN EMAIL, lowercased and trimmed — not
 *  `users.id`, which exists for four people in the whole region (see the spec's
 *  2026-09-05 correction). `me` must come from the authenticated session and
 *  never from a record about a child; that provenance rule is what makes
 *  sending an email here acceptable at all. */
export function identifyUser(input: { surface: Surface; me: Pick<Me, 'email' | 'roles'> }): void {
  if (!active) return
  const email = input.me.email?.trim().toLowerCase()
  if (!email) return
  if (identified === email) return
  identified = email
  posthog.identify(email, { email, roles: input.me.roles, surface: input.surface })
}
